import { Head, Link, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { AppShell } from "@/components/AppShell"
import { ClipboardList } from "lucide-react"
import { PageHeader } from "@/components/PageHeader"
import { EmptyState } from "@/components/blocks/EmptyState"
import { Button } from "@/components/ui/button"
import { UnlockPrompt } from "@/components/UnlockPrompt"
import { ExamList } from "@/components/exams/ExamList"
import type { ExamSummary } from "@/types/exams"
import type { PageProps } from "@/types/inertia"

export default function PracticeExamsIndex({
  exam_version_name,
  exams,
}: {
  exam_version_name: string | null
  exams: ExamSummary[]
}) {
  const intl = useIntl()
  const programAccess = usePage<PageProps>().props.current_user?.program_access ?? false
  const description = intl.formatMessage({ id: "exams.index_meta" })
  const title = intl.formatMessage({ id: "exams.title" })
  const version = exam_version_name ?? intl.formatMessage({ id: "exams.version_fallback" })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <PageHeader
          title={title}
          description={intl.formatMessage({ id: "exams.subtitle" }, { version })}
        />
        <div className="mt-8">
          {!programAccess ? (
            <UnlockPrompt what={intl.formatMessage({ id: "exams.unlock_what" })} />
          ) : exams.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title={intl.formatMessage({ id: "exams.empty_title" })}
              description={intl.formatMessage({ id: "exams.empty_body" }, { version })}
              action={
                <Button asChild variant="secondary">
                  <Link href="/settings">{intl.formatMessage({ id: "exams.open_settings" })}</Link>
                </Button>
              }
            />
          ) : (
            <ExamList exams={exams} hrefFor={(exam) => `/practice_exams/${exam.id}`} />
          )}
        </div>
      </AppShell>
    </>
  )
}
